import { Activity, MessagesSquare, Sparkles, Zap } from 'lucide-react';
import { ActivityListSkeleton, StatCardsSkeleton } from '@/components/feedback/Skeletons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useActivity, useAgents, useSessions } from '@/api/queries';

export function DashboardPage() {
  const { data: agents, isLoading: agentsLoading } = useAgents();
  const { data: sessions, isLoading: sessionsLoading } = useSessions('open');
  const { data: activity, isLoading: activityLoading } = useActivity(25);
  const statsLoading = agentsLoading || sessionsLoading || activityLoading;

  const aiCount = agents?.filter((a) => a.type === 'AI').length ?? 0;
  const activeCount = agents?.filter((a) => a.status_badge === 'active' || a.status_badge === 'busy').length ?? 0;

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
        <p className="text-sm text-muted-foreground">Real-time status of the Multi-Agent System.</p>
      </header>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {statsLoading ? (
          <StatCardsSkeleton />
        ) : (
          <>
            <StatCard icon={<Sparkles className="h-4 w-4" />} label="AI agents" value={aiCount} />
            <StatCard icon={<Zap className="h-4 w-4" />} label="Active / busy" value={activeCount} />
            <StatCard
              icon={<MessagesSquare className="h-4 w-4" />}
              label="Open sessions"
              value={sessions?.length ?? 0}
            />
            <StatCard
              icon={<Activity className="h-4 w-4" />}
              label="Events (last 25)"
              value={activity?.items.length ?? 0}
            />
          </>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent activity</CardTitle>
        </CardHeader>
        <CardContent>
          {activityLoading ? (
            <ActivityListSkeleton rows={8} itemClassName="py-2" />
          ) : activity?.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing yet.</p>
          ) : (
            <ul className="divide-y text-sm">
              {activity?.items.slice(0, 8).map((entry) => (
                <li key={entry.id} className="flex items-center gap-3 py-2">
                  <span className="w-32 truncate text-xs text-muted-foreground">
                    {new Date(entry.ts).toLocaleTimeString()}
                  </span>
                  <span className="font-medium">{entry.from ?? '—'}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="font-medium">{entry.to ?? '—'}</span>
                  <span className="ml-3 flex-1 truncate text-muted-foreground">{entry.summary}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="grid h-10 w-10 place-items-center rounded-md bg-primary/10 text-primary">
          {icon}
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-xl font-semibold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
